import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../../models/Logging';
import { useErrorMessages, useTextMessaging } from '../../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ConversationItem } from '../../ConversationItem';
import { useTextMessageConversationsContext } from '../context';
import { styleSheet } from '../styles';

export const ConversationList: React.FC = observer(() => {
	const textMessagingVM = useTextMessaging();
	const {
		selectedConversation,
		selectedRecipient,
		setRecipients,
		setSelectedConversation,
		setSelectedRecipient,
		setSelectedRecipientIndex,
		setShowNewConversationSearch,
	} = useTextMessageConversationsContext();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging();
	const history = useHistory();

	const onConversationClick = (conversation: Api.ConversationViewModel) => {
		setShowNewConversationSearch(false);
		if (selectedConversation?.id !== conversation.id) {
			setRecipients([]);

			history.push(`/texting/${textMessagingVM.id}/${conversation.id}`);
		}
	};

	const onTrashConversationClick = async (conversation: Api.ConversationViewModel) => {
		try {
			await textMessagingVM.archiveConversation(conversation.id);
			if (selectedConversation?.id === conversation.id) {
				setSelectedConversation(null);

				setSelectedRecipientIndex(null);
				history.push('/texting');
			}

			toast.success('Conversation deleted.');
		} catch (err) {
			logger.logApiError('ArchiveTextConversation-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const onMarkConversationAsSpamClick = async (conversation: Api.ConversationViewModel) => {
		try {
			await textMessagingVM.markAsSpam(conversation.id);
			if (selectedConversation?.id === conversation.id) {
				setSelectedConversation(null);

				setSelectedRecipient(null);
				history.push('/texting');
			}

			toast.success('Conversation marked as spam.');
		} catch (err) {
			logger.logApiError('MarkAsSpamConversation-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const onUnsubscribeConversationClick = async (conversation: Api.ConversationViewModel) => {
		// create an unsubscribed variable (and pass to conversation item) based on the existance of automatedSMSOptOutDate
		// if it exists on the conversation then the conversation is unsubscribed.
		try {
			await textMessagingVM.toggleUnsubscribeConversation(conversation);
			await selectedRecipient?.load();

			toast.success(`Conversation ${conversation.automatedSMSOptOutDate ? 'un' : ''}subscribed.`);
		} catch (err) {
			logger.logApiError('UnsubscribedConvo-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const onMarkConversationAsUnreadClick = async (conversation: Api.ConversationViewModel) => {
		try {
			await conversation.markAsUnread();
			toast.success('Conversation marked as unread.');
		} catch (err) {
			logger.logApiError('MarkAsUnreadConversation-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const loadMoreConversations = () => {
		textMessagingVM.getConversations().catch(err => errorMessages.pushApiError(Api.asApiError(err)));
	};

	const isLoading = textMessagingVM.loadingConversations && !textMessagingVM.conversations?.length;

	const allLoaded = !textMessagingVM.allConversationsLoaded;

	const noConversations =
		textMessagingVM.conversationsLoaded &&
		!textMessagingVM.loadingConversations &&
		textMessagingVM.conversations.length === 0;

	const filterByLast = (conversation: Api.ConversationViewModel) => conversation.lastMessage;

	return (
		<div className={css(styleSheet.conversationsContainer)}>
			{noConversations && <div className={css(styleSheet.noConversations)}>No conversations</div>}

			{textMessagingVM.conversations.filter(filterByLast).map(conversation => (
				<ConversationItem
					className={css(styleSheet.conversationItem)}
					conversation={conversation}
					key={conversation.id}
					onClick={onConversationClick}
					onMarkAsSpamClick={onMarkConversationAsSpamClick}
					onMarkAsUnreadClick={onMarkConversationAsUnreadClick}
					onTrashClick={onTrashConversationClick}
					onUnsubscribeClick={onUnsubscribeConversationClick}
					selected={selectedConversation?.id === conversation.id}
					selectedRecipient={selectedRecipient}
				/>
			))}
			{allLoaded && <Waypoint key='conversations-waypoint' bottomOffset='-200px' onEnter={loadMoreConversations} />}
			{isLoading && <LoadingSpinner className={css(styleSheet.conversationsLoading)} />}
		</div>
	);
});
